import type { CalendarStyleId } from '../../types/schema'

/**
 * Calendar styles are display-only: they change how event chips render, not
 * the page background. Surface / text / grid / halo colors are inherited from
 * the active app theme (see index.css `--surface-*`, `--text-*`, `--border-*`,
 * `--accent`), so the calendar automatically matches Light, Dark, OLED,
 * Midnight, Aurora, Ember, and Celestial without per-theme presets.
 */
export interface CalendarStyleMeta {
  id: CalendarStyleId
  label: string
  /** Small preview color used in the style picker swatch. */
  swatch: string
  /** Whether this style layers translucent surfaces + backdrop-filter. */
  glass: boolean
}

export const CALENDAR_STYLES: CalendarStyleMeta[] = [
  {
    id: 'cards',
    label: 'Cards',
    swatch: 'linear-gradient(135deg,#7c5ae0,#40e8c0)',
    glass: false,
  },
  {
    id: 'glass',
    label: 'Glass',
    swatch: 'linear-gradient(135deg,rgba(124,90,224,0.55),rgba(64,232,192,0.35))',
    glass: true,
  },
]

export const DEFAULT_CALENDAR_STYLE: CalendarStyleId = 'cards'

/** Kind colors are style-agnostic — event dots / accents pull from these. */
export const CALENDAR_KIND_COLORS = {
  goal: '#8b6df0',
  task: '#34d399',
  subtask: '#fbbf24',
  checklist: '#c084fc',
  meeting: '#38bdf8',
  session: '#f43f5e',
  edu_event: '#ec4899',
  edu_ptm: '#a855f7',
} as const

const LEGACY_STYLE_ALIAS: Record<string, CalendarStyleId> = {
  clean: 'cards',
  mint: 'cards',
  midnight: 'glass',
  aurora: 'glass',
}

export const normalizeCalendarStyleId = (value: unknown): CalendarStyleId => {
  if (typeof value !== 'string') return DEFAULT_CALENDAR_STYLE
  const direct = CALENDAR_STYLES.find((style) => style.id === value)
  if (direct) return direct.id
  return LEGACY_STYLE_ALIAS[value] ?? DEFAULT_CALENDAR_STYLE
}

export const getCalendarStyle = (id: CalendarStyleId | null | undefined): CalendarStyleMeta =>
  CALENDAR_STYLES.find((style) => style.id === id) ?? CALENDAR_STYLES[0]
